import type { TenantBranding } from '@dentexe/types';
import { create } from 'zustand';
import { applyOrganizationTheme } from '@/theme/apply-organization-theme';
import { DEFAULT_ORGANIZATION_THEME, DEFAULT_ORG_THEME_COLORS } from '@/theme/defaults';
import { normalizeHexColor } from '@/theme/color-scale';
import type { OrganizationTheme, OrganizationThemeColors } from '@/theme/types';

interface OrganizationThemeState extends OrganizationTheme {
  isLoaded: boolean;
  initialize: () => void;
  setForOrganization: (
    organizationId: string,
    branding: Pick<TenantBranding, 'clinicName' | 'logoUrl' | 'primaryColor'> & {
      accentColor?: string;
    },
  ) => void;
  reset: () => void;
}

function brandingToColors(branding: {
  primaryColor?: string;
  accentColor?: string;
}): OrganizationThemeColors {
  return {
    primary: normalizeHexColor(branding.primaryColor, DEFAULT_ORG_THEME_COLORS.primary),
    accent: normalizeHexColor(branding.accentColor, DEFAULT_ORG_THEME_COLORS.accent),
  };
}

function commitOrganizationTheme(state: OrganizationTheme): void {
  applyOrganizationTheme(state.colors);
}

export const useOrganizationThemeStore = create<OrganizationThemeState>((set) => ({
  ...DEFAULT_ORGANIZATION_THEME,
  isLoaded: false,

  initialize() {
    const fallback = DEFAULT_ORGANIZATION_THEME;
    commitOrganizationTheme(fallback);
    set({ ...fallback, isLoaded: true });
  },

  setForOrganization(organizationId, branding) {
    set((state) => {
      const next: OrganizationTheme = {
        organizationId,
        clinicName: branding.clinicName ?? state.clinicName,
        logoUrl: branding.logoUrl !== undefined ? (branding.logoUrl ?? null) : state.logoUrl,
        colors: brandingToColors({
          primaryColor: branding.primaryColor ?? state.colors.primary,
          accentColor: branding.accentColor ?? state.colors.accent,
        }),
      };
      commitOrganizationTheme(next);
      return { ...next, isLoaded: true };
    });
  },

  reset() {
    const fallback = DEFAULT_ORGANIZATION_THEME;
    commitOrganizationTheme(fallback);
    set({ ...fallback, isLoaded: false });
  },
}));

/** Call after login when JWT carries organization branding. */
export function syncOrganizationThemeFromBranding(
  organizationId: string,
  branding?: TenantBranding,
): void {
  useOrganizationThemeStore.getState().setForOrganization(organizationId, {
    clinicName: branding?.clinicName,
    logoUrl: branding?.logoUrl,
    primaryColor: branding?.primaryColor,
    accentColor: branding?.accentColor,
  });
}
